import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Divider, Flex, Text } from '@chakra-ui/react';
import { MdShowChart } from 'react-icons/md';
import { RiBarChartFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';

import {
  getChartFilter,
  getModelDetails,
  overallTimeFilter,
  setChartFilter,
} from 'redux/PlanPageSlice';
import {
  getCompareChartFilter,
  getCompareScenario,
  getVarianceFilterChart,
  setCompareChartFilter,
  toggleVarianceFilterChart,
} from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_SEGMENT_ACTIVE_SHADOW,
  PLAN_SEGMENT_TRACK_BG,
  PLAN_TEXT,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

import PlanCardToolbarStrip from './PlanCardToolbarStrip';

import type { PlanTimeFilterValue } from './PlanTimeFilterSegment';

const CHART_TYPES = [
  { key: 'line', label: 'Line Chart', Icon: MdShowChart },
  { key: 'bar', label: 'Bar Chart', Icon: RiBarChartFill },
] as const;

interface Properties {
  viewMode: string;
  onChange: (value: string) => void;
  onDownload: () => void;
  isExporting?: boolean;
}

/**
 * Chart card's toolbar — composed from the same `PlanCardToolbarStrip` the
 * table card uses (Figma's two cards share one toolbar pattern). The chart
 * has no meaningful "search" (it shows one series at a time, driven by the
 * KPI row above it, not a list of named rows) so the search box is simply
 * omitted rather than wired to nothing. The bar/line toggle and the
 * compare-mode variance `%` button ride in as `leadingActions` — same
 * dispatches as before, just composed into the shared strip instead of a
 * hand-rolled row.
 */
const PlanChartToolbar = ({ viewMode, onChange, onDownload, isExporting = false }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const modelDetail = useSelector(getModelDetails);
  const selectedYear = useSelector(overallTimeFilter);
  const planChartFilter = useSelector(getChartFilter);
  const compareChartFilter = useSelector(getCompareChartFilter);
  const varianceFilter = useSelector(getVarianceFilterChart);
  const compareScenario = useSelector(getCompareScenario);
  const activeChartFilter = (
    compareScenario ? compareChartFilter : planChartFilter
  ) as PlanTimeFilterValue;

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const [displayLevels, setDisplayLevels] = useState<PlanTimeFilterValue>(activeChartFilter);

  useEffect(() => {
    setDisplayLevels(activeChartFilter);
  }, [activeChartFilter]);

  useEffect(() => {
    if (displayLevels === 'Y' && selectedYear !== 'all') {
      setDisplayLevels('M');
      if (compareScenario) {
        dispatch(setCompareChartFilter('M'));
      } else {
        dispatch(setChartFilter('M'));
      }
    }
  }, [compareScenario, dispatch, displayLevels, selectedYear]);

  const onChangeSummaryMode = useCallback(
    (value: PlanTimeFilterValue) => {
      setDisplayLevels(value);
      if (compareScenario) {
        dispatch(setCompareChartFilter(value));
        return;
      }
      dispatch(setChartFilter(value));
    },
    [compareScenario, dispatch],
  );

  const onToggleVariance = useCallback(() => {
    dispatch(toggleVarianceFilterChart());
  }, [dispatch]);

  return (
    <PlanCardToolbarStrip
      hint='Select a metric above to update the chart'
      showTimeToggle={timeAggregation !== 'Y'}
      timeValue={displayLevels}
      onTimeChange={onChangeSummaryMode}
      onDownload={onDownload}
      isExporting={isExporting}
      hintTrailing={
        <Flex
          alignItems='center'
          bg={PLAN_SEGMENT_TRACK_BG}
          border='1px solid'
          borderColor={neutral.border}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          p='2px'
          gap='2px'
          flexShrink={0}
        >
          {CHART_TYPES.map(({ key, label, Icon }) => {
            const isActive = viewMode === key;

            return (
              <Flex
                key={key}
                as='button'
                type='button'
                aria-label={label}
                alignItems='center'
                gap='6px'
                h='26px'
                px='10px'
                borderRadius='6px'
                bg={isActive ? neutral.surface : 'transparent'}
                boxShadow={isActive ? PLAN_SEGMENT_ACTIVE_SHADOW : 'none'}
                cursor='pointer'
                transition='background 0.15s ease, box-shadow 0.15s ease'
                _hover={isActive ? undefined : { bg: 'rgba(255,255,255,0.6)' }}
                onClick={() => onChange(key)}
              >
                <Icon size={13} color={isActive ? accent.solid : textToken.secondary} />
                <Text
                  {...PLAN_TEXT.labelSmMedium}
                  color={isActive ? accent.solid : textToken.secondary}
                  whiteSpace='nowrap'
                >
                  {label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      }
      leadingActions={
        compareScenario ? (
          <Flex alignItems='center' gap={3}>
            <Button
              size='sm'
              h='28px'
              minH='28px'
              w='28px'
              minW='28px'
              p={0}
              {...PLAN_TEXT.labelSmMedium}
              borderRadius='8px'
              border='1px solid'
              borderColor={varianceFilter ? accent.solid : neutral.border}
              bg={neutral.surface}
              color={accent.solid}
              boxShadow='none'
              _hover={{ borderColor: accent.solid }}
              onClick={onToggleVariance}
            >
              %
            </Button>
            <Divider orientation='vertical' h='20px' borderColor={neutral.border} />
          </Flex>
        ) : undefined
      }
    />
  );
};

export default memo(PlanChartToolbar);
